import * as React from 'react';
import { Drawer, Collapse, Menu } from '@mui/material';
import MediaQuery from '../../../../MediaQuery/MediaQuery';
import { useTranslation } from 'react-i18next';
import cn from 'classnames';
import { useTheme } from '../../../../theme';
import Input from '../../../../BaseComponents/Input';
import { useCallback, useRef } from 'react';
import FlightListItem from '../FlightListItem/FlightListItem';
import Slider from 'react-slick';
import { CloseClear, Filter, Land, Search, Takeoff } from '../../../../Icons';
import LastUpdate from '../LastUpdate/LastUpdate';
import { format, parse } from 'date-fns';
import { SERVER_DATE_FORMAT } from '../../services/constants';
import { useDispatch } from 'react-redux';
import { setHoveredFlight } from '../../store/flightStatus/flightStatus';
import { useFlightStatusUIState } from './Header.hooks';
import Link from '../../../../Link';
import { Locale } from '../../../../types';
import MenuItem from '@material-ui/core/MenuItem';
const Header = () => {
    const DELIMITER_DATE_FORMAT = 'dd LLL, EEE';
    const { Header: css } = useTheme('FlightStatus');
    const mobileFiltersTriggerRef = useRef();
    const { t } = useTranslation('FlightStatus');
    const dispatch = useDispatch();
    const inputRef = useRef(null);
    const handleSetHoveredFlight = (flight) => dispatch(setHoveredFlight(flight));
    const { locale, onInputChange, handleClearInput, filteredFlights, inputValue, isCollapseOpen, isDrawerOpen, openCollapse, isScrollBarVisible, handleChangeFilter, isNeedToRenderFilters, currentFilter, isNeedToRenderDelimiter, openedFlightId, lastUpdate, mobileFilters } = useFlightStatusUIState();
    // handle hover only if flight in sky
    const handleFlightHover = useCallback((flight) => {
        handleSetHoveredFlight(flight);
    }, [handleSetHoveredFlight]);
    const handleFocus = useCallback(() => {
        var _a, _b;
        if (inputRef.current) {
            (_b = (_a = inputRef.current) === null || _a === void 0 ? void 0 : _a.querySelector('input')) === null || _b === void 0 ? void 0 : _b.focus();
        }
    }, [inputRef]);
    const renderAdornment = () => {
        const showClearIcon = !!inputValue;
        return (React.createElement("div", { className: cn(css.closeIcon, {
                [css.searchAdornment]: !showClearIcon
            }), onClick: showClearIcon ? handleClearInput : handleFocus }, showClearIcon ? CloseClear : Search));
    };
    const renderInput = () => {
        return (React.createElement(Input, { className: css.inputWrapper, label: t('City / Airport / Flight number'), onChange: onInputChange, InputLabelProps: {
                classes: {
                    root: css.label
                }
            }, InputProps: {
                ref: inputRef,
                endAdornment: renderAdornment()
            } }));
    };
    const filtersMap = new Map([
        ['all', 'All'],
        ['departure', 'Departing'],
        ['arrival', 'Arriving']
    ]);
    const filtersIconByFilterType = new Map([
        ['all', Filter],
        ['departure', Takeoff],
        ['arrival', Land]
    ]);
    const renderFilters = useCallback(() => {
        return (React.createElement(React.Fragment, null,
            React.createElement(MediaQuery, { minWidth: 'mobile' },
                React.createElement("div", { className: css.filters }, [...filtersMap.entries()].map(([key]) => (React.createElement(Link, { key: key, className: cn(css.filter, {
                        [css.active]: currentFilter === key
                    }), action: () => handleChangeFilter(key) },
                    key !== 'all' && filtersIconByFilterType.get(key),
                    t(key)))))),
            React.createElement(MediaQuery, { maxWidth: 'mobile' },
                React.createElement("div", { className: css.mobileFilters },
                    React.createElement("div", { ref: mobileFiltersTriggerRef, onClick: mobileFilters.open, className: css.mobileFiltersTrigger },
                        filtersIconByFilterType.get(currentFilter),
                        t(currentFilter)),
                    React.createElement(Menu, { open: mobileFilters.isOpen, anchorEl: mobileFiltersTriggerRef.current, onClose: mobileFilters.close }, [...filtersMap.entries()].map(([key]) => (React.createElement(MenuItem, { key: key, selected: key === currentFilter, onClick: () => handleChangeFilter(key), className: css.mobileFiltersItem },
                        key !== 'all' && filtersIconByFilterType.get(key),
                        t(key)))))))));
    }, [isNeedToRenderFilters, currentFilter, handleChangeFilter, mobileFilters]);
    const renderDelimiter = (flight) => {
        return (React.createElement("div", { className: css.delimiter },
            React.createElement("span", { className: css.delimiterText }, locale === Locale.Russian
                ? format(parse(flight.departure.date, SERVER_DATE_FORMAT, new Date()), DELIMITER_DATE_FORMAT).replace('.', '')
                : format(parse(flight.departure.date, SERVER_DATE_FORMAT, new Date()), DELIMITER_DATE_FORMAT))));
    };
    return (React.createElement("div", { className: css.header, onClick: e => e.stopPropagation() },
        React.createElement("div", { className: css.inner },
            React.createElement(MediaQuery, { minWidth: 'mobile' },
                React.createElement("div", { className: css.title }, t('Flight Status'))),
            React.createElement(MediaQuery, { maxWidth: 'mobile' },
                React.createElement("div", { className: css.mobileHeaderContent },
                    React.createElement("div", { className: css.title }, t('Flight Status')),
                    !!inputValue && isNeedToRenderFilters && renderFilters(),
                    React.createElement("div", { className: css.searchIcon, onClick: () => {
                            openCollapse();
                            setTimeout(handleFocus, 0);
                        } }, Search)),
                React.createElement(Collapse, { classes: {
                        root: cn(css.collapse, { [css.collapseActive]: isCollapseOpen })
                    }, in: isCollapseOpen }, renderInput())),
            React.createElement("div", { className: css.right },
                React.createElement(MediaQuery, { minWidth: 'mobile' },
                    React.createElement("div", { className: css.rightWrapper },
                        React.createElement(LastUpdate, { className: css.lastUpdate, lastUpdate: lastUpdate }),
                        renderInput())),
                React.createElement(MediaQuery, { minWidth: 'mobile' },
                    React.createElement(Drawer, { disableEnforceFocus: true, hideBackdrop: true, disableAutoFocus: true, classes: {
                            root: css.drawerContainer,
                            paper: cn(css.drawerPaper, { [css.scroll]: !isScrollBarVisible })
                        }, open: isDrawerOpen, anchor: 'right' },
                        React.createElement(React.Fragment, null,
                            renderFilters(), filteredFlights === null || filteredFlights === void 0 ? void 0 :
                            filteredFlights.map((flight, flightIndex) => (React.createElement(React.Fragment, { key: flight.uniqueId },
                                flightIndex === 0 ? renderDelimiter(flight) : null,
                                React.createElement(FlightListItem, { scrollToItemWhenActive: true, isActive: flight.uniqueId === openedFlightId, className: css.listItem, flight: flight, selectFlight: handleFlightHover }),
                                isNeedToRenderDelimiter(flight, flightIndex)
                                    ? renderDelimiter(filteredFlights[flightIndex + 1])
                                    : null))))))),
            React.createElement(MediaQuery, { maxWidth: 'mobile' },
                React.createElement("div", { className: css.sliderContainer },
                    React.createElement(Slider, { slidesToShow: 1.3, swipe: true, infinite: false }, filteredFlights === null || filteredFlights === void 0 ? void 0 : filteredFlights.map(flight => (React.createElement(React.Fragment, { key: flight.uniqueId },
                        React.createElement(FlightListItem, { isActive: openedFlightId === flight.uniqueId, className: cn(css.listItem), flight: flight, selectFlight: handleFlightHover }))))))))));
};
export default Header;
